import { getApiBaseUrl } from '../config/apiBaseUrl';

/**
 * Thin typed fetch wrapper around the backend REST API (U52).
 *
 * The per-endpoint method bodies are deliberately untyped (`Promise<any>`) for
 * now — the backend response schemas are not stable enough to lock down, and
 * pinning them all to `unknown` would force hundreds of call-site casts in
 * one commit.  Instead we expose a generic `request<T>()` so new call sites
 * can opt in to type safety:
 *
 *     const policies = await apiService.request<Policy[]>('/policies');
 *
 * Over time individual methods can be narrowed (e.g. `getPolicy(id): Promise<Policy>`)
 * without touching consumers.
 */

type QueryParams =
  | URLSearchParams
  | Record<string, string | number | boolean | null | undefined>;

type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  /** Abort the request after this many ms (default: no timeout). */
  timeoutMs?: number;
};

function paramsToQuery(params: QueryParams | undefined): URLSearchParams {
  if (!params) return new URLSearchParams();
  if (params instanceof URLSearchParams) return params;
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    usp.append(k, String(v));
  });
  return usp;
}

class APIService {
  private readonly baseURL: string;

  constructor() {
    this.baseURL = getApiBaseUrl();
  }

  private _getHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    const activeCompanyId = localStorage.getItem('activeCompanyId');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(activeCompanyId ? { 'X-Company-Id': activeCompanyId } : {}),
    };
  }

  private _getUploadHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    const activeCompanyId = localStorage.getItem('activeCompanyId');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(activeCompanyId ? { 'X-Company-Id': activeCompanyId } : {}),
    };
  }

  /**
   * Generic typed request helper. Pass `T` to have the parsed JSON body
   * returned as that type:  `await apiService.request<Foo>('/foo')`.
   */
  async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const { timeoutMs, ...rest } = options;

    const controller = timeoutMs ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

    const defaultOptions: RequestInit = {
      headers: this._getHeaders(),
      credentials: 'include',
      ...(controller ? { signal: controller.signal } : {}),
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...rest });
      const data = (await response.json().catch(() => ({}))) as any;
      if (!response.ok) {
        const detail = data && data.message ? ` — ${data.message}` : '';
        const err = new Error(`HTTP error! status: ${response.status}${detail}`) as Error & {
          status?: number;
          body?: any;
        };
        err.status = response.status;
        err.body = data;
        throw err;
      }
      return data as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // ─── Auth endpoints ───────────────────────────────────────
  /** POST /auth/login — returns { success, token, user, company, companies? } or { totp_required: true } */
  async login(email: string, password: string, totpToken?: string): Promise<any> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(totpToken ? { totp_token: totpToken } : {}) }),
    });
  }

  /**
   * POST /auth/register — public self-service registration is disabled by
   * default in production (returns 404). Kept for legacy callers / tests.
   */
  async register(userData: any): Promise<any> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /** GET /auth/me — current user, company, and (for superadmin) all companies */
  async getMe(): Promise<any> {
    return this.request('/auth/me');
  }

  /** POST /auth/logout — server-side adds the bearer token to the denylist. */
  async logout(): Promise<any> {
    return this.request('/auth/logout', { method: 'POST' });
  }

  /** POST /auth/password/forgot — always 204 (no enumeration). */
  async requestPasswordReset(email: string): Promise<any> {
    return this.request('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /** POST /auth/password/reset — completes the flow with the emailed token. */
  async resetPassword(token: string, password: string): Promise<any> {
    return this.request('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  /** POST /auth/email/send-verification — auth required. */
  async sendEmailVerification(): Promise<any> {
    return this.request('/auth/email/send-verification', { method: 'POST' });
  }

  /** POST /auth/email/verify — completes email verification. */
  async verifyEmail(token: string): Promise<any> {
    return this.request('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // ─── Admin endpoints ──────────────────────────────────────
  async getAdminStats(): Promise<any> {
    return this.request('/admin/stats');
  }

  async getCompanyProfile(): Promise<any> {
    return this.request('/company/profile');
  }

  async getCompanyCertifications(): Promise<any> {
    return this.request('/company/certifications');
  }

  async getAdminCertificationCatalog(): Promise<any> {
    return this.request('/admin/certifications/catalog');
  }

  async getAdminCustomerCertifications(customerId: string | number): Promise<any> {
    return this.request(`/admin/customers/${customerId}/certifications`);
  }

  async previewAdminCertificationDelta(customerId: string | number, body: any): Promise<any> {
    return this.request(`/admin/customers/${customerId}/certifications/preview-delta`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateAdminCustomerCertifications(customerId: string | number, body: any): Promise<any> {
    return this.request(`/admin/customers/${customerId}/certifications`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  // ------------------------------------------------------------------
  // §8.4 Evidence automation
  // ------------------------------------------------------------------
  async getEvidenceDashboard(auditFocus?: string): Promise<any> {
    const params = new URLSearchParams();
    if (auditFocus) params.set('audit_focus', auditFocus);
    const qs = params.toString();
    return this.request(`/evidence/dashboard${qs ? `?${qs}` : ''}`);
  }

  async getEvidenceConnectors(): Promise<any> {
    return this.request('/evidence/connectors');
  }

  async getAvailableConnectors(): Promise<any> {
    return this.request('/evidence/connectors/_/available');
  }

  async createEvidenceConnector(data: any): Promise<any> {
    return this.request('/evidence/connectors', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateEvidenceConnector(id: string, data: any): Promise<any> {
    return this.request(`/evidence/connectors/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteEvidenceConnector(id: string): Promise<any> {
    return this.request(`/evidence/connectors/${id}`, { method: 'DELETE' });
  }

  async runEvidenceConnector(id: string): Promise<any> {
    return this.request(`/evidence/connectors/${id}/run`, { method: 'POST' });
  }

  async getEvidenceItems(params: { kind?: string; source?: string; limit?: number } = {}): Promise<any> {
    const q = new URLSearchParams();
    if (params.kind) q.set('kind', params.kind);
    if (params.source) q.set('source', params.source);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return this.request(`/evidence/items${qs ? '?' + qs : ''}`);
  }

  async getControlRules(): Promise<any> {
    return this.request('/evidence/rules');
  }

  async createControlRule(data: any): Promise<any> {
    return this.request('/evidence/rules', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateControlRule(id: string, data: any): Promise<any> {
    return this.request(`/evidence/rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteControlRule(id: string): Promise<any> {
    return this.request(`/evidence/rules/${id}`, { method: 'DELETE' });
  }

  async evaluateControlRules(): Promise<any> {
    return this.request('/evidence/rules/evaluate', { method: 'POST' });
  }

  async aiDraftPolicy(payload: {
    kind: 'policy' | 'sop' | 'register' | 'template' | 'training';
    topic: string;
    frameworks?: string[];
    clauses?: string[];
    audience?: string[];
    additional_context?: string;
    temperature?: number;
    template_key?: string;
  }): Promise<any> {
    return this.request('/policies/ai-draft', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCompanyProfile(data: any): Promise<any> {
    return this.request('/company/profile', { method: 'PATCH', body: JSON.stringify(data) });
  }

  async getCustomers(params: QueryParams = {}): Promise<any> {
    const q = paramsToQuery(params);
    return this.request(`/admin/customers?${q}`);
  }

  async getCustomer(id: string | number): Promise<any> {
    return this.request(`/admin/customers/${id}`);
  }

  async createCustomer(data: any): Promise<any> {
    return this.request('/admin/customers', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateCustomer(id: string | number, data: any): Promise<any> {
    return this.request(`/admin/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteCustomer(id: string | number): Promise<any> {
    return this.request(`/admin/customers/${id}`, { method: 'DELETE' });
  }

  async uploadCustomerLogo(companyId: string | number, file: File): Promise<any> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('logo', file);
    const res = await fetch(`${this.baseURL}/admin/customers/${companyId}/logo`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || `Upload failed (${res.status})`);
    }
    return data;
  }

  async deleteCustomerLogo(companyId: string | number): Promise<any> {
    return this.request(`/admin/customers/${companyId}/logo`, { method: 'DELETE' });
  }

  async getCustomerUsers(companyId: string | number): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users`);
  }

  async addCustomerUser(companyId: string | number, data: any): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateCustomerUser(companyId: string | number, userId: string | number, data: any): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteCustomerUser(companyId: string | number, userId: string | number): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users/${userId}`, { method: 'DELETE' });
  }

  async getActivityLogs(params: QueryParams = {}): Promise<any> {
    const q = paramsToQuery(params);
    return this.request(`/admin/activity-logs?${q}`);
  }

  // ─── Team management endpoints ──────────────────────────────
  async getTeamMembers(): Promise<any> {
    return this.request('/team');
  }

  async addTeamMember(data: any): Promise<any> {
    return this.request('/team', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTeamMember(userId: string | number, data: any): Promise<any> {
    return this.request(`/team/${userId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteTeamMember(userId: string | number): Promise<any> {
    return this.request(`/team/${userId}`, { method: 'DELETE' });
  }

  async getTeamMemberSharedPolicies(userId: string | number): Promise<any> {
    return this.request(`/team/${userId}/shared-policies`);
  }

  async updateTeamMemberSharedPolicies(userId: string | number, policyIds: Array<string | number>): Promise<any> {
    return this.request(`/team/${userId}/shared-policies`, {
      method: 'PUT',
      body: JSON.stringify({ policyIds }),
    });
  }

  /** Superadmin: list policy rows for picker (no company context header). */
  async getAdminCustomerPolicyList(companyId: string | number): Promise<any> {
    return this.request(`/admin/customers/${companyId}/policies`);
  }

  async getAdminCustomerSharedPolicies(companyId: string | number, userId: string | number): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users/${userId}/shared-policies`);
  }

  async updateAdminCustomerSharedPolicies(
    companyId: string | number,
    userId: string | number,
    policyIds: Array<string | number>
  ): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users/${userId}/shared-policies`, {
      method: 'PUT',
      body: JSON.stringify({ policyIds }),
    });
  }

  // --- Phase 2 support tooling ---
  async adminResetUserPassword(companyId: string, userId: string, password: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async adminRevokeSessions(companyId: string, userId: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/users/${userId}/revoke-sessions`, {
      method: 'POST',
    });
  }

  async adminLogEnter(companyId: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/log-enter`, { method: 'POST' });
  }

  async adminGetNotes(companyId: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/notes`);
  }

  async adminAddNote(companyId: string, note: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async adminDeleteNote(companyId: string, noteId: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/notes/${noteId}`, { method: 'DELETE' });
  }

  async adminGetTimeline(companyId: string, page = 1): Promise<any> {
    return this.request(`/admin/customers/${companyId}/timeline?page=${page}&limit=50`);
  }

  // Phase 4 — Content & Data Management
  async adminProvision(companyId: string): Promise<any> {
    return this.request(`/admin/customers/${companyId}/provision`, { method: 'POST' });
  }

  async adminExportCustomer(companyId: string): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${this.baseURL}/admin/customers/${companyId}/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as any).message || 'Export failed');
    }
    return res.blob();
  }

  async adminBulkImport(csvFile: File): Promise<any> {
    const token = this.getToken();
    const form = new FormData();
    form.append('csv', csvFile);
    const res = await fetch(`${this.baseURL}/admin/customers/bulk-import`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Bulk import failed');
    return data;
  }

  async adminGetPolicyTemplates(params?: { search?: string; category?: string }): Promise<any> {
    const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString() : '';
    return this.request(`/admin/policy-templates${qs ? `?${qs}` : ''}`);
  }

  async adminPushTemplates(companyId: string, templateKeys: string[]): Promise<any> {
    return this.request(`/admin/customers/${companyId}/push-template`, {
      method: 'POST',
      body: JSON.stringify({ template_keys: templateKeys }),
    });
  }

  // --- Readiness walkthrough (internal audit-style self-review) ---
  async fetchReadinessWalkthrough(auditFocus?: string): Promise<any> {
    const params = new URLSearchParams();
    if (auditFocus) params.set('audit_focus', auditFocus);
    const qs = params.toString();
    return this.request(`/readiness/walkthrough${qs ? `?${qs}` : ''}`);
  }

  async fetchControlReadiness(controlId: string): Promise<any> {
    return this.request(`/readiness/controls/${encodeURIComponent(controlId)}`);
  }

  async saveControlReview(
    controlId: string,
    payload: { verdict: string; comment?: string | null }
  ): Promise<any> {
    return this.request(
      `/readiness/controls/${encodeURIComponent(controlId)}/review`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }
    );
  }

  async uploadControlEvidence(
    controlId: string,
    payload: { file?: File | null; evidenceSlug?: string | null; description?: string }
  ): Promise<any> {
    const formData = new FormData();
    if (payload.file) formData.append('file', payload.file);
    if (payload.evidenceSlug) formData.append('evidence_slug', payload.evidenceSlug);
    if (payload.description) formData.append('description', payload.description);
    const res = await fetch(
      `${this.baseURL}/readiness/controls/${encodeURIComponent(controlId)}/evidence`,
      {
        method: 'POST',
        headers: this._getUploadHeaders(),
        body: formData,
      }
    );
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Failed to upload evidence');
    }
    return data;
  }

  async deleteControlEvidence(evidenceId: string): Promise<any> {
    return this.request(
      `/readiness/evidence/${encodeURIComponent(evidenceId)}`,
      { method: 'DELETE' }
    );
  }

  getControlEvidenceDownloadUrl(evidenceId: string): string {
    return `${this.baseURL}/readiness/evidence/${encodeURIComponent(evidenceId)}/download`;
  }

  // --- Controls ---
  async getControls(category?: string, searchQuery?: string): Promise<any> {
    let endpoint = '/controls';
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (searchQuery) params.append('q', searchQuery);
    if (params.toString()) endpoint += `?${params.toString()}`;
    return this.request(endpoint);
  }

  async getResponses(controlId: string | number): Promise<any> {
    return this.request(`/controls/responses/${controlId}`);
  }

  async saveResponses(controlId: string | number, responses: any): Promise<any> {
    return this.request('/controls/responses', {
      method: 'POST',
      body: JSON.stringify({ controlId, responses }),
    });
  }

  async submitQuestionResponse(controlId: string | number, responseData: any): Promise<any> {
    return this.request(`/controls/responses/${controlId}`, {
      method: 'POST',
      body: JSON.stringify(responseData),
    });
  }

  async generateDocument(controlId: string | number, templateName: string): Promise<any> {
    return this.request('/generate-document', {
      method: 'POST',
      body: JSON.stringify({ controlId, templateName }),
    });
  }

  async getControlAuditHistory(controlId: string | number): Promise<any> {
    try {
      const response = await this.request(`/controls/${controlId}/audit-history`);
      return response;
    } catch (error) {
      console.error('Error fetching audit history:', error);
      throw error;
    }
  }

  async getControlStatus(controlId: string | number): Promise<any> {
    try {
      const response = await this.request(`/controls/${controlId}/status`);
      return response;
    } catch (error) {
      console.error('Error fetching control status:', error);
      throw error;
    }
  }

  async getCompanyControlStatus(controlId: string | number): Promise<any> {
    try {
      const response = await this.request(`/controls/company-control/${controlId}`);
      return response;
    } catch (error) {
      console.error('Error fetching company control status:', error);
      throw error;
    }
  }

  async updateControlStatus(
    controlId: string | number,
    { status, note }: { status: string; note?: string }
  ): Promise<any> {
    try {
      const response = await this.request(`/controls/${controlId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status, note }),
      });
      return response;
    } catch (error) {
      console.error('Error updating control status:', error);
      throw error;
    }
  }

  // --- Audit Evidence ---
  async getAuditEvidence(controlId: string | number, auditId?: string | number): Promise<any> {
    const params = new URLSearchParams();
    if (auditId) params.append('audit_id', String(auditId));
    const qs = params.toString();
    return this.request(`/controls/${controlId}/evidence${qs ? `?${qs}` : ''}`);
  }

  async addAuditEvidence(controlId: string | number, evidence: Record<string, unknown>): Promise<any> {
    return this.request(`/controls/${controlId}/evidence`, {
      method: 'POST',
      body: JSON.stringify(evidence),
    });
  }

  async uploadAuditEvidence(
    controlId: string | number,
    formData: FormData
  ): Promise<any> {
    const url = `${this.baseURL}/controls/${controlId}/evidence`;
    const res = await fetch(url, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      credentials: 'include',
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || data.message || 'Failed to upload evidence');
    }
    return data;
  }

  async updateAuditEvidence(id: string | number, evidence: any): Promise<any> {
    return this.request(`/controls/evidence/${id}`, {
      method: 'PUT',
      body: JSON.stringify(evidence),
    });
  }

  async deleteAuditEvidence(id: string | number): Promise<any> {
    return this.request(`/controls/evidence/${id}`, { method: 'DELETE' });
  }

  async getBulkEvidenceCounts(auditId?: string | number): Promise<any> {
    const params = new URLSearchParams();
    if (auditId) params.append('audit_id', String(auditId));
    const qs = params.toString();
    return this.request(`/controls/evidence/bulk-counts${qs ? `?${qs}` : ''}`);
  }

  async evaluateEvidence(evidenceId: string | number, evaluation: any): Promise<any> {
    return this.request(`/controls/evidence/${evidenceId}/evaluate`, {
      method: 'POST',
      body: JSON.stringify(evaluation),
    });
  }

  // --- Audit Findings ---
  async getAuditFindings(controlId: string | number, auditId?: string | number): Promise<any> {
    const params = new URLSearchParams();
    if (auditId) params.append('audit_id', String(auditId));
    const qs = params.toString();
    return this.request(`/controls/${controlId}/findings${qs ? `?${qs}` : ''}`);
  }

  async addAuditFinding(controlId: string | number, finding: any): Promise<any> {
    return this.request(`/controls/${controlId}/findings`, {
      method: 'POST',
      body: JSON.stringify(finding),
    });
  }

  async updateAuditFinding(id: string | number, finding: any): Promise<any> {
    return this.request(`/controls/findings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(finding),
    });
  }

  async deleteAuditFinding(id: string | number): Promise<any> {
    return this.request(`/controls/findings/${id}`, { method: 'DELETE' });
  }

  async submitCorrectiveAction(findingId: string | number, correctiveAction: any): Promise<any> {
    return this.request(`/controls/findings/${findingId}/corrective-action`, {
      method: 'POST',
      body: JSON.stringify(correctiveAction),
    });
  }

  async verifyCorrectiveAction(findingId: string | number, verification: any): Promise<any> {
    return this.request(`/controls/findings/${findingId}/verify`, {
      method: 'POST',
      body: JSON.stringify(verification),
    });
  }

  // --- Audit Management ---
  async getAudits(): Promise<any> {
    return this.request('/audits');
  }

  async getAudit(auditId: string | number): Promise<any> {
    return this.request(`/audits/${auditId}`);
  }

  async updateAuditStage(
    auditId: string | number,
    stage: string,
    notes?: string
  ): Promise<any> {
    return this.request(`/audits/${auditId}/stage`, {
      method: 'POST',
      body: JSON.stringify({ stage, notes }),
    });
  }

  async generateAuditReport(auditId: string | number): Promise<any> {
    return this.request(`/audits/${auditId}/report`);
  }

  // --- Risk Assessment Management ---
  async getRiskAssessments(filters: QueryParams = {}): Promise<any> {
    const params = paramsToQuery(filters);
    return this.request(`/risk-assessments?${params}`);
  }

  async getRiskAssessment(id: string | number): Promise<any> {
    return this.request(`/risk-assessments/${id}`);
  }

  async createRiskAssessment(riskAssessmentData: any): Promise<any> {
    return this.request('/risk-assessments', {
      method: 'POST',
      body: JSON.stringify(riskAssessmentData),
    });
  }

  async updateRiskAssessment(id: string | number, riskAssessmentData: any): Promise<any> {
    return this.request(`/risk-assessments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(riskAssessmentData),
    });
  }

  async deleteRiskAssessment(id: string | number): Promise<any> {
    return this.request(`/risk-assessments/${id}`, { method: 'DELETE' });
  }

  async getRiskAssessmentForControl(controlId: string | number): Promise<any> {
    return this.request(`/risk-assessments/control/${controlId}`);
  }

  async calculateRiskBasedSampling(samplingParams: any): Promise<any> {
    return this.request('/risk-assessments/calculate-sampling', {
      method: 'POST',
      body: JSON.stringify(samplingParams),
    });
  }

  async getRiskDashboardMetrics(): Promise<any> {
    return this.request('/risk-assessments/dashboard/metrics');
  }

  // --- Audit Program Management ---
  async getAuditPrograms(filters: QueryParams = {}): Promise<any> {
    const params = paramsToQuery(filters);
    return this.request(`/audit-programs?${params}`);
  }

  async getAuditProgram(id: string | number): Promise<any> {
    return this.request(`/audit-programs/${id}`);
  }

  async createAuditProgram(programData: any): Promise<any> {
    return this.request('/audit-programs', {
      method: 'POST',
      body: JSON.stringify(programData),
    });
  }

  async updateAuditProgram(id: string | number, programData: any): Promise<any> {
    return this.request(`/audit-programs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(programData),
    });
  }

  async deleteAuditProgram(id: string | number): Promise<any> {
    return this.request(`/audit-programs/${id}`, { method: 'DELETE' });
  }

  async approveAuditProgram(id: string | number, approvalNotes?: string): Promise<any> {
    return this.request(`/audit-programs/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approval_notes: approvalNotes }),
    });
  }

  async rejectAuditProgram(id: string | number, rejectionReason?: string): Promise<any> {
    return this.request(`/audit-programs/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejection_reason: rejectionReason }),
    });
  }

  async getAuditProgramCalendar(id: string | number): Promise<any> {
    return this.request(`/audit-programs/${id}/calendar`);
  }

  async assignAuditorToProgram(programId: string | number, auditorData: any): Promise<any> {
    return this.request(`/audit-programs/${programId}/assign-auditor`, {
      method: 'POST',
      body: JSON.stringify(auditorData),
    });
  }

  async removeAuditorFromProgram(programId: string | number, userId: string | number): Promise<any> {
    return this.request(`/audit-programs/${programId}/auditor/${userId}`, {
      method: 'DELETE',
    });
  }

  async getAuditProgramDashboardMetrics(): Promise<any> {
    return this.request('/audit-programs/dashboard/metrics');
  }

  // --- Policy Management ---
  async getPolicies(filters: QueryParams = {}): Promise<any> {
    const params = paramsToQuery(filters);
    return this.request(`/policies?${params}`);
  }

  /** Management programs (certification scopes) for the current company */
  async getManagementPrograms(): Promise<any> {
    return this.request('/policies/reference/management-programs');
  }

  async getPolicy(id: string | number): Promise<any> {
    return this.request(`/policies/${id}`);
  }

  /** Same-company users for reviewer / approver assignment */
  async getPolicyTeamUsers(): Promise<any> {
    return this.request('/policies/team-users');
  }

  /** Assigned reviewer marks review complete → workflow moves to awaiting approval */
  async completePolicyReview(id: string | number): Promise<any> {
    return this.request(`/policies/${id}/workflow/complete-review`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async createPolicy(policyData: any): Promise<any> {
    return this.request('/policies', {
      method: 'POST',
      body: JSON.stringify(policyData),
    });
  }

  async updatePolicy(id: string | number, policyData: any): Promise<any> {
    return this.request(`/policies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(policyData),
    });
  }

  async deletePolicy(id: string | number): Promise<any> {
    return this.request(`/policies/${id}`, { method: 'DELETE' });
  }

  /** Replace legacy opaque document_id with POL-ORG/ABBREV/NUM */
  async regeneratePolicyDocumentId(id: string | number): Promise<any> {
    return this.request(`/policies/${id}/regenerate-document-id`, {
      method: 'POST',
    });
  }

  async approvePolicy(id: string | number, approvalNotes?: string): Promise<any> {
    return this.request(`/policies/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approval_notes: approvalNotes }),
    });
  }

  async publishPolicy(id: string | number, effectiveDate?: string): Promise<any> {
    return this.request(`/policies/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify({ effective_date: effectiveDate }),
    });
  }

  async mapControlToPolicy(policyId: string | number, mappingData: any): Promise<any> {
    return this.request(`/policies/${policyId}/map-control`, {
      method: 'POST',
      body: JSON.stringify(mappingData),
    });
  }

  async unmapControlFromPolicy(policyId: string | number, controlId: string | number): Promise<any> {
    return this.request(`/policies/${policyId}/unmap-control/${controlId}`, {
      method: 'DELETE',
    });
  }

  async getPolicyDashboardMetrics(): Promise<any> {
    return this.request('/policies/dashboard/metrics');
  }

  async getPolicyGaps(standards?: string): Promise<any> {
    const params = new URLSearchParams();
    if (standards) params.set('standards', standards);
    const qs = params.toString();
    return this.request(`/policies/gaps${qs ? `?${qs}` : ''}`);
  }

  async getPolicyAuditMatrix(auditFocus: string): Promise<any> {
    const params = new URLSearchParams();
    if (auditFocus) params.set('audit_focus', auditFocus);
    return this.request(`/policies/audit-matrix?${params}`);
  }

  async getPolicyStandardsMatrix(policyId: string | number): Promise<any> {
    return this.request(`/policies/${policyId}/standards-matrix`);
  }

  async exportAuditPack(auditFocus?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (auditFocus) params.set('audit_focus', auditFocus);
    const qs = params.toString();
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${this.baseURL}/policies/audit-pack/export${qs ? `?${qs}` : ''}`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Audit pack export failed');
    }
    return res.blob();
  }

  async getCertificationBillingLimit(): Promise<any> {
    return this.request('/billing/certification-limit');
  }

  async getPolicyReviewCalendar(year?: number | string, month?: number | string): Promise<any> {
    const params = new URLSearchParams();
    if (year !== undefined && year !== null && year !== '') params.append('year', String(year));
    if (month !== undefined && month !== null && month !== '') params.append('month', String(month));
    return this.request(`/policies/review-calendar?${params}`);
  }

  async analyzeGapImport(payload: {
    template_key: string;
    content_text?: string;
    file?: File | null;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('template_key', payload.template_key);
    if (payload.content_text) formData.append('content_text', payload.content_text);
    if (payload.file) formData.append('file', payload.file);
    const res = await fetch(`${this.baseURL}/policies/gap-import/analyze`, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Gap import analysis failed');
    return data;
  }

  async previewGapImportMerge(payload: {
    template_key: string;
    content_text: string;
    section_analysis?: unknown;
    wizard_answers?: Record<string, unknown>;
  }): Promise<any> {
    return this.request('/policies/gap-import/preview-merge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async applyGapImportAnswers(
    policyId: string | number,
    wizardAnswers: Record<string, unknown>
  ): Promise<any> {
    return this.request(`/policies/${policyId}/gap-import/apply-answers`, {
      method: 'POST',
      body: JSON.stringify({ wizard_answers: wizardAnswers }),
    });
  }

  async getPolicyGapFollowUps(policyId: string | number): Promise<any> {
    return this.request(`/policies/${policyId}/gap-follow-ups`);
  }

  async completeGapImport(payload: {
    template_key: string;
    content_text: string;
    wizard_answers?: Record<string, unknown>;
    section_analysis?: unknown;
    content_override?: string;
    file?: File | null;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('template_key', payload.template_key);
    formData.append('content_text', payload.content_text);
    if (payload.wizard_answers) {
      formData.append('wizard_answers', JSON.stringify(payload.wizard_answers));
    }
    if (payload.section_analysis) {
      formData.append('section_analysis', JSON.stringify(payload.section_analysis));
    }
    if (payload.content_override) {
      formData.append('content_override', payload.content_override);
    }
    if (payload.file) formData.append('file', payload.file);
    const res = await fetch(`${this.baseURL}/policies/gap-import/complete`, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to save imported policy');
    return data;
  }

  async bulkUploadPolicies(files: FileList | File[]): Promise<any> {
    const formData = new FormData();
    const arr = Array.from(files as ArrayLike<File>);
    for (let i = 0; i < arr.length; i++) {
      formData.append('files', arr[i]);
    }
    const res = await fetch(`${this.baseURL}/policies/bulk-upload`, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Bulk upload failed');
    return data;
  }

  async uploadPolicyZip(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('zip', file);
    const res = await fetch(`${this.baseURL}/policies/upload-zip`, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Zip upload failed');
    return data;
  }

  async uploadPolicyFile(policyId: string | number, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${this.baseURL}/policies/${policyId}/upload`, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Upload failed');
    return data;
  }

  // --- Policy Templates & Wizard ---
  async getPolicyTemplates(): Promise<any> {
    return this.request('/policies/templates');
  }

  async getPolicyTemplate(key: string): Promise<any> {
    return this.request(`/policies/templates/${key}`);
  }

  async getIsoClauses(): Promise<any> {
    return this.request('/policies/reference/iso-clauses');
  }

  async getComplianceFrameworks(): Promise<any> {
    return this.request('/policies/reference/frameworks');
  }

  /** Full Annex A register (93 controls) — for linking library documents to controls */
  async getPolicyAnnexControls(): Promise<any> {
    return this.request('/policies/reference/annex-controls');
  }

  /** §8.3 library kind labels (policy | sop | register | template | training) */
  async getLibraryKinds(): Promise<any> {
    return this.request('/policies/reference/library-kinds');
  }

  async createPolicyFromTemplate(
    templateKey: string,
    wizardAnswers: any,
    overrides: any = {}
  ): Promise<any> {
    return this.request('/policies/from-template', {
      method: 'POST',
      body: JSON.stringify({ template_key: templateKey, wizard_answers: wizardAnswers, overrides }),
    });
  }

  // --- §8.1 Multi-framework support ---
  /** List active compliance frameworks (Framework rows). */
  async getFrameworks(auditFocus?: string): Promise<any> {
    const params = new URLSearchParams();
    if (auditFocus) params.set('audit_focus', auditFocus);
    const qs = params.toString();
    return this.request(`/frameworks${qs ? `?${qs}` : ''}`);
  }

  /** Get a framework + all its clauses by slug. */
  async getFramework(slug: string): Promise<any> {
    return this.request(`/frameworks/${encodeURIComponent(slug)}`);
  }

  /** Per-clause coverage for the current company. */
  async getFrameworkCoverage(slug: string): Promise<any> {
    return this.request(`/frameworks/${encodeURIComponent(slug)}/coverage`);
  }

  /** All cross-framework mappings for one Annex A control id (e.g. '5.1'). */
  async getControlFrameworkMappings(controlId: string): Promise<any> {
    return this.request(`/frameworks/_/control-mappings/${encodeURIComponent(controlId)}`);
  }

  async createControlFrameworkMapping(payload: {
    control_id: string;
    framework_clause_id: string;
    coverage?: 'full' | 'partial' | 'compensating';
    notes?: string;
  }): Promise<any> {
    return this.request('/frameworks/_/control-mappings', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteControlFrameworkMapping(id: string): Promise<any> {
    return this.request(`/frameworks/_/control-mappings/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // --- Scheduled activities ---
  async getActivities(params: QueryParams = {}): Promise<any> {
    const q = paramsToQuery(params);
    return this.request(`/activities?${q}`);
  }

  async getActivitiesDue(): Promise<any> {
    return this.request('/activities/due');
  }

  async getActivityReminders(days: number = 7): Promise<any> {
    const q = new URLSearchParams({ days: String(days) });
    return this.request(`/activities/reminders?${q}`);
  }

  async createActivity(data: any): Promise<any> {
    return this.request('/activities', { method: 'POST', body: JSON.stringify(data) });
  }

  async getActivity(id: string | number): Promise<any> {
    return this.request(`/activities/${id}`);
  }

  async updateActivity(id: string | number, data: any): Promise<any> {
    return this.request(`/activities/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async completeActivity(id: string | number, notes?: string): Promise<any> {
    return this.request(`/activities/${id}/complete`, { method: 'POST', body: JSON.stringify({ notes }) });
  }

  async deleteActivity(id: string | number): Promise<any> {
    return this.request(`/activities/${id}`, { method: 'DELETE' });
  }

  // ─── Generic convenience methods ─────────────────────────────
  async get<T = any>(endpoint: string, params?: QueryParams): Promise<{ data: T }> {
    const q = paramsToQuery(params);
    const qs = q.toString();
    const data = await this.request<T>(`${endpoint}${qs ? '?' + qs : ''}`);
    return { data };
  }

  async post<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
    return { data };
  }

  async patch<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
    });
    return { data };
  }

  async put<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    });
    return { data };
  }

  async del<T = any>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, { method: 'DELETE' });
    return { data };
  }

  // ─── Smart Policy Import ──────────────────────────────────────────────────

  /** POST /policies/smart-import/analyze — text input */
  async smartImportAnalyzeText(text: string): Promise<any> {
    return this.request('/policies/smart-import/analyze', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  /** POST /policies/smart-import/analyze — file upload */
  async smartImportAnalyzeFile(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${this.baseURL}/policies/smart-import/analyze`, {
      method: 'POST',
      headers: this._getUploadHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Analysis failed');
    return data;
  }

  /** POST /policies/smart-import/create-batch — create all reviewed policies */
  async smartImportCreateBatch(policies: any[]): Promise<any> {
    return this.request('/policies/smart-import/create-batch', {
      method: 'POST',
      body: JSON.stringify({ policies }),
    });
  }

  async downloadPolicyFile(policyId: string | number, filename?: string): Promise<void> {
    const res = await fetch(`${this.baseURL}/policies/${policyId}/download`, {
      headers: this._getUploadHeaders(),
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'policy-document';
    a.click();
    window.URL.revokeObjectURL(url);
  }
}

const apiService = new APIService();
export default apiService;
export { APIService };
